/**
 * Zoom / pan of the Logo inspector's compare panes.
 *
 * A view maps a content point c (pane pixels at zoom 1) to `t + zoom × c`.
 *
 * The panes used to zoom with `transform: scale()` alone. Chromium paints a
 * transformed layer once and magnifies that bitmap, so a vector trace never got
 * redrawn at the zoomed size: its edges showed the 1× pixels as stair-steps,
 * while Firefox/Safari re-render and looked sharp. Now the zoom is COMMITTED as
 * real layout (the box is laid out `zoom` times larger, so the browser draws the
 * SVG at its true on-screen size) and the transform only bridges the gap while
 * the wheel is still turning — cheap during the gesture, crisp once it settles.
 */
export interface ZoomView {
  zoom: number;
  tx: number;
  ty: number;
}

export const ZOOM_LIMITS = { min: 1, max: 12 } as const;
export const IDENTITY_VIEW: ZoomView = { zoom: 1, tx: 0, ty: 0 };

/** Zoom by `factor` keeping the content under pane point `p` where it is. */
export function zoomAt(view: ZoomView, p: { x: number; y: number }, factor: number): ZoomView {
  const zoom = Math.min(Math.max(view.zoom * factor, ZOOM_LIMITS.min), ZOOM_LIMITS.max);
  if (zoom === view.zoom) return view;
  if (zoom === ZOOM_LIMITS.min) return { ...IDENTITY_VIEW };
  return {
    zoom,
    tx: p.x - ((p.x - view.tx) / view.zoom) * zoom,
    ty: p.y - ((p.y - view.ty) / view.zoom) * zoom,
  };
}

/**
 * Styles that show `live` while the box is laid out at `committed`: the box
 * sits at the committed offset and size, and a transform makes up the rest.
 * Once committed === live the transform is the identity and nothing is a
 * magnified bitmap any more.
 */
export function zoomBoxStyle(live: ZoomView, committed: ZoomView): Record<string, string> {
  const s = live.zoom / committed.zoom;
  const dx = live.tx - committed.tx;
  const dy = live.ty - committed.ty;
  return {
    left: `${committed.tx}px`,
    top: `${committed.ty}px`,
    width: `${committed.zoom * 100}%`,
    height: `${committed.zoom * 100}%`,
    // Content inset (padding) scales with the box so the zoom stays linear.
    '--zoom': String(committed.zoom),
    transform: s === 1 && dx === 0 && dy === 0 ? 'none' : `translate(${dx}px, ${dy}px) scale(${s})`,
  };
}
